import { HighlightedSentence } from '@litlingo/react';
// @ts-ignore
import DocumentViz from '@litlingo/react/dist/DocumentViz';
import { previewFeedback } from 'actions';
import logo from 'assets/logo.png';
import {
  THUMBS_DOWN_FILL_ICON,
  THUMBS_DOWN_ICON,
  THUMBS_UP_FILL_ICON,
  THUMBS_UP_ICON,
} from 'constants/commonIcons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomerDomain, getUser } from 'selectors/auth';
import {
  getAnnotatedTestSentenceResultV2,
  getCommunicationTest,
  getDisableFeedback,
  getGraphAnnotatedTestSentenceResultV2,
  getRulesViolatedV2,
  getSelectedCustomer,
  getSelectedPlatform,
  getTranslationLanguage,
} from 'selectors/communications';
import { getNavParams } from 'selectors/nav';
import { getPermissionsPolicy } from 'selectors/permissions';
import { getFieldAuthorization } from 'utils/permissions';

type ComponentProps = {
  showHighlights?: boolean;
  annotationBasePath?: string;
  blockHighlights?: boolean;
};

const TestResultsVisualizer: React.FC<ComponentProps> = ({
  showHighlights = true,
  annotationBasePath,
  blockHighlights = false,
}) => {
  const dispatch = useDispatch();

  const communication = useSelector(getCommunicationTest);
  const user = useSelector(getUser);
  const policy = useSelector(getPermissionsPolicy);
  const entityFieldLevel = getFieldAuthorization('communications.entity', user.roles, policy);
  const customerDomain = useSelector(getCustomerDomain);

  const resultGraphV2 = useSelector(getGraphAnnotatedTestSentenceResultV2);
  const resultLinesV2 = useSelector(getAnnotatedTestSentenceResultV2);
  const translationLanguage = useSelector(getTranslationLanguage);

  const bodyParam = (useSelector(getNavParams).body_visualizer || '') as string;
  const { rulesViolated: rulesViolatedV2 } = useSelector(getRulesViolatedV2);
  const platform = useSelector(getSelectedPlatform);
  const selectedCustomer = useSelector(getSelectedCustomer);
  const disableFeedback = useSelector(getDisableFeedback);

  const graph = resultGraphV2;
  const lines = resultLinesV2;

  const [selectedThumbs, setSelectedThumbs] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    setSelectedThumbs(null);
  }, [resultGraphV2]);

  const handleSelectThumbs = (value: 'up' | 'down'): void => {
    if (selectedThumbs === value) {
      setSelectedThumbs(null);
    } else {
      setSelectedThumbs(value);
      dispatch(
        previewFeedback({
          body: bodyParam,
          acceptable: value === 'up',
          ...(selectedCustomer ? { customer_uuid: selectedCustomer } : {}),
          ...(platform ? { platform } : {}),
          rule_hits: rulesViolatedV2,
        })
      );
    }
  };

  if (!lines) {
    return (
      <div className="w-3/4 text-body flex flex-col gap-2">
        <span className="font-bold">Highlighted matches</span>
        <div className="pt-4 border-t border-gray-2 mr-4 italic">
          Your test sentence and <span className="bg-litlingo-gray-1">any matches</span> will be
          highlighted here.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-8">
      {showHighlights && lines && (
        <div className="w-full flex flex-row gap-4">
          <div className="w-3/4 text-body flex flex-col gap-2">
            <div className="flex flex-row justify-between items-center pr-2">
              <span className="font-bold">Highlighted matches</span>
              <div className="flex flex-row gap-4">
                <button
                  type="button"
                  className="focus:outline-none"
                  onClick={(): void => handleSelectThumbs('up')}
                  disabled={disableFeedback}
                >
                  {selectedThumbs === 'up' ? THUMBS_UP_FILL_ICON : THUMBS_UP_ICON}
                </button>
                <button
                  type="button"
                  className="focus:outline-none"
                  onClick={(): void => handleSelectThumbs('down')}
                  disabled={disableFeedback}
                >
                  {selectedThumbs === 'down' ? THUMBS_DOWN_FILL_ICON : THUMBS_DOWN_ICON}
                </button>
              </div>
            </div>
            <div className="border-t border-gray-2" />
            <div className="mt-2 min-h-11 mr-4">
              {lines.map((line, idx) => (
                <div key={line.index} className={idx !== 0 ? 'mt-2' : ''}>
                  <HighlightedSentence
                    nodes={line.nodes}
                    useHover={user.roles.includes('super-admin')}
                    protectedFields={{
                      entity: entityFieldLevel,
                    }}
                    translationLanguage={translationLanguage}
                    blockHighlight={blockHighlights}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="w-1/4" />
        </div>
      )}

      <div className="w-full">
        {communication && (
          <>
            <div className="flex flex-col gap-3 text-body">
              <span className="font-bold">Language Visualizer</span>
              {graph && (
                <div className="rounded border border-litlingo-gray-2">
                  <DocumentViz
                    annotationsBasePath={annotationBasePath || `/${customerDomain}/annotator`}
                    logo={logo}
                    lines={graph.lines}
                    arrows={graph.arrows}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TestResultsVisualizer;
